import { Injectable, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from '../shared/notifications/notification.service';
import { MfaReminderDialogComponent, MfaReminderResult } from './mfa-reminder-dialog/mfa-reminder-dialog.component';
import { AuthService } from '../shared/authentication/auth.service';
import { AppConfig } from '../app.config';

@Injectable({ providedIn: 'root' })
export class MfaService {
  private readonly MFA_MANAGEMENT_PAGE_URL = AppConfig.hosts.alderApp + '/account/manage';

  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly notificationService = inject(NotificationService);

  private readonly incomplete = signal(false);

  private snoozeKey(): string {
    return `mfa-reminder-snoozed-${this.authService.user.snapshot?.identity?.id ?? 'anon'}`;
  }

  constructor() {
    this.notificationService.registerRule({
      id: 'mfa-incomplete',
      condition: () => this.incomplete(),
      notification: {
        type: 'account',
        section: 'security',
        title: 'Finish setting up MFA',
        message: 'Multi Factor Authentication will be required starting May 15, 2026.',
        createdAt: new Date(),
        action: () => this.openDialog(),
      },
    });
  }

  /** Call after user loads — recomputes enrollment state and prompts if needed. */
  async syncStatus(): Promise<void> {
    const incomplete =
      !this.authService.isSuperAdmin() &&
      !this.authService.isImpersonating() &&
      this.authService.hasMfa() &&
      !this.authService.usesMfa();

    this.incomplete.set(incomplete);

    if (incomplete && !sessionStorage.getItem(this.snoozeKey())) {
      this.openDialog();
    }
  }

  manageMfa(): void {
    globalThis.window.open(this.MFA_MANAGEMENT_PAGE_URL, '_blank');
  }

  openDialog(): void {
    this.dialog
      .open(MfaReminderDialogComponent, { width: '520px', autoFocus: false, disableClose: false })
      .afterClosed()
      .subscribe((result: MfaReminderResult) => {
        if (result === 'setup') {
          this.manageMfa();
        } else {
          sessionStorage.setItem(this.snoozeKey(), '1');
        }
      });
  }
}
